/**
 * PasteIsinArea — alternative to the file dropzone: accept a raw text
 * paste of ISINs, ignore everything that doesn't match the regex, and
 * trigger the same backend job as the file upload.
 *
 * Phase 5 follow-up: this is a QUICK INPUT, not a surface. The textarea keeps a
 * compact fixed height and no longer stretches to match a neighbouring panel.
 */
import { useMemo, useState } from "react";
import { Loader2, ClipboardPaste, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

const ISIN_RE = /\b[A-Z]{2}[A-Z0-9]{9}[0-9]\b/g;

export function PasteIsinArea({ onSubmit, uploading, disabled = false }) {
  const [text, setText] = useState("");
  const locked = uploading || disabled;

  const previewIsins = useMemo(() => {
    const matches = (text || '').toUpperCase().match(ISIN_RE) || [];
    return Array.from(new Set(matches));
  }, [text]);

  const submit = () => {
    if (previewIsins.length === 0 || locked) return;
    onSubmit(text);
  };

  // A7 — the disabled action always states its reason.
  const blockedReason = uploading
    ? "Importazione in corso: attendi che finisca."
    : disabled
    ? "Non disponibile durante un'analisi in corso."
    : text.trim() && previewIsins.length === 0
    ? "Nel testo incollato non c'è nessun ISIN valido (2 lettere + 10 caratteri)."
    : !text.trim()
    ? "Incolla almeno un ISIN per abilitare l'importazione."
    : null;

  return (
    <div className="flex flex-col gap-2">
      <p className="text-content-secondary text-xs">
        Uno per riga, oppure separati da virgola, punto e virgola, tabulazione o
        spazi. Tutto ciò che non è un ISIN valido viene ignorato.
      </p>
      <textarea
        data-testid="bulk-isin-paste-textarea"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={"IE00B4L5Y983\nLU1681043599, IE00BK5BC677"}
        disabled={locked}
        rows={3}
        className="h-[92px] w-full px-3 py-2 bg-surface-subtle border border-line rounded-lg text-sm text-content placeholder:text-content-muted font-mono focus:outline-none focus-visible:border-focus focus-visible:ring-2 focus-visible:ring-focus/40 resize-y disabled:opacity-60"
      />
      {/* Phase 6 — at 390px count, reason and CTA get their own lines instead
          of three squeezed columns. */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
        <p
          data-testid="bulk-isin-paste-count"
          className="text-xs flex items-center gap-1.5 text-content-muted"
        >
          <Sparkles className="w-3.5 h-3.5" aria-hidden="true" />
          {previewIsins.length === 0
            ? 'Nessun ISIN rilevato'
            : `${previewIsins.length} ISIN rilevat${previewIsins.length === 1 ? 'o' : 'i'}`}
        </p>
        {blockedReason && (
          <p
            id="paste-isin-reason"
            data-testid="paste-isin-reason"
            className="text-xs text-content-muted sm:text-right sm:flex-1"
          >
            {blockedReason}
          </p>
        )}
        <Button
          data-testid="bulk-isin-paste-submit"
          size="sm"
          onClick={submit}
          aria-describedby={blockedReason ? "paste-isin-reason" : undefined}
          disabled={previewIsins.length === 0 || locked}
          className="h-10 w-full sm:w-auto bg-brand hover:bg-brand-hover text-content-on-brand disabled:opacity-40"
        >
          {uploading
            ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Avvio…</>
            : <><ClipboardPaste className="w-3.5 h-3.5 mr-1.5" />Importa ISIN</>}
        </Button>
      </div>
    </div>
  );
}
